/**
 * M7 — Banki párosító oldal
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { get, post } from '../../lib/api';
import { getAccessToken } from '../../lib/auth';
import { PageHeader, Button, Badge, Card, Tabs } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { useFeature } from '../../hooks/useFeature';
import LockedFeature from '../../components/LockedFeature';

// ── Típusok ────────────────────────────────────────────────────────────────────

interface BankTransaction {
  id: string;
  source: string;
  transactionDate: string;
  amountHuf: number;
  description: string | null;
  reference: string | null;
  counterpartyName: string | null;
  counterpartyAccount: string | null;
  status: 'PAROSITATLAN' | 'PAROSITVA' | 'KERDESES' | 'KIHAGYVA';
  matchedInvoiceId: string | null;
  createdAt: string;
}

interface MatchSuggestion {
  invoiceId: string;
  invoiceNumber: string;
  partnerName: string;
  dueDate: string;
  amountHuf: number;
  openAmountHuf: number;
  score: number;
  confidence: 'AUTO' | 'VALOSZINU' | 'KERDESES';
}

interface PendingMatch {
  transaction: BankTransaction;
  suggestions: MatchSuggestion[];
}

interface ImportResult { imported: number; skipped: number; total: number; }
interface AutoMatchResult { matched: number; suggestions: number; }
interface DunningResult { sent: number; }

// ── Segédfüggvények ────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  PAROSITATLAN: 'Párosítatlan', PAROSITVA: 'Párosítva',
  KERDESES: 'Kérdéses', KIHAGYVA: 'Kihagyva',
};
const STATUS_COLOR: Record<string, 'warning' | 'success' | 'error' | 'info'> = {
  PAROSITATLAN: 'warning', PAROSITVA: 'success',
  KERDESES: 'info', KIHAGYVA: 'error',
};
const CONF_COLOR: Record<string, 'success' | 'warning' | 'info'> = {
  AUTO: 'success', VALOSZINU: 'warning', KERDESES: 'info',
};
const CONF_LABEL: Record<string, string> = {
  AUTO: 'Biztos', VALOSZINU: 'Valószínű', KERDESES: 'Kérdéses',
};

function fmtHuf(n: number) {
  return n.toLocaleString('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 });
}
function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('hu-HU');
}

// ── Upload tab ─────────────────────────────────────────────────────────────────

function UploadTab() {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function handleUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast('Válassz ki egy fájlt!', 'error'); return; }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/bank-transactions/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken()}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Ismeretlen hiba' }));
        throw new Error(err.error ?? 'Import hiba');
      }
      const data: ImportResult = await res.json();
      setResult(data);
      toast(`${data.imported} tranzakció importálva (${data.skipped} duplikált kihagyva).`, 'success');
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card style={{ maxWidth: 520 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>Banki kivonat importálása</h3>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Elfogadott formátumok: CSV (pontosvesszős vagy vesszős), MT940 (.sta), CAMT.053 (ISO 20022 XML)
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input ref={fileRef} type="file" accept=".csv,.sta,.xml,.mt940"
          style={{ flex: 1, fontSize: 13 }} />
        <Button onClick={handleUpload} disabled={loading} loading={loading}>
          Importálás
        </Button>
      </div>
      {result && (
        <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--success-bg, #f0fdf4)', borderRadius: 8, fontSize: 13 }}>
          <strong>{result.imported}</strong> új tranzakció importálva,{' '}
          <strong>{result.skipped}</strong> duplikált kihagyva (összesen {result.total} sor feldolgozva).
        </div>
      )}
    </Card>
  );
}

// ── Pending tab ────────────────────────────────────────────────────────────────

function PendingTab({ onMatched }: { onMatched: () => void }) {
  const { toast } = useToast();
  const [items, setItems] = useState<PendingMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRunning, setAutoRunning] = useState(false);
  const [dunningRunning, setDunningRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<PendingMatch[]>('/bank-transactions/pending');
      setItems(data);
    } catch { toast('Betöltési hiba.', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleMatch(txId: string, invoiceId: string) {
    try {
      await post(`/bank-transactions/${txId}/match`, { invoiceId });
      toast('Párosítva!', 'success');
      onMatched();
      load();
    } catch (e) { toast((e as Error).message, 'error'); }
  }

  async function handleSkip(txId: string) {
    try {
      await post(`/bank-transactions/${txId}/skip`, {});
      toast('Kihagyva.', 'info');
      load();
    } catch (e) { toast((e as Error).message, 'error'); }
  }

  async function handleAutoMatch() {
    setAutoRunning(true);
    try {
      const r = await post<AutoMatchResult>('/bank-transactions/auto-match', {});
      toast(`Auto-párosítás: ${r.matched} párosítva, ${r.suggestions} javaslat elmentve.`, 'success');
      load();
    } catch (e) { toast((e as Error).message, 'error'); }
    finally { setAutoRunning(false); }
  }

  async function handleDunning() {
    setDunningRunning(true);
    try {
      const r = await post<DunningResult>('/bank-transactions/run-dunning', {});
      toast(`Fizetési emlékeztető: ${r.sent} értesítés elküldve.`, 'success');
    } catch (e) { toast((e as Error).message, 'error'); }
    finally { setDunningRunning(false); }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <Button onClick={handleAutoMatch} disabled={autoRunning} loading={autoRunning} variant="primary">
          Auto-párosítás futtatása
        </Button>
        <Button onClick={handleDunning} disabled={dunningRunning} loading={dunningRunning} variant="secondary">
          Fizetési emlékeztető küldése
        </Button>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
          Nincs párosítandó tranzakció.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {items.map(({ transaction: tx, suggestions }) => (
            <Card key={tx.id} style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {fmtDate(tx.transactionDate)}&nbsp;&nbsp;
                    <span style={{ color: tx.amountHuf >= 0 ? 'var(--success)' : 'var(--error)' }}>
                      {fmtHuf(tx.amountHuf)}
                    </span>
                  </div>
                  {tx.counterpartyName && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {tx.counterpartyName}
                      {tx.counterpartyAccount && ` — ${tx.counterpartyAccount}`}
                    </div>
                  )}
                  {tx.description && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.description}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Badge color={STATUS_COLOR[tx.status]}>{STATUS_LABEL[tx.status]}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => handleSkip(tx.id)}>Kihagyás</Button>
                </div>
              </div>

              {suggestions.length > 0 && (
                <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Javaslatok
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {suggestions.map(s => (
                      <div key={s.invoiceId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: 'var(--surface-secondary, #f9fafb)', borderRadius: 6, padding: '8px 10px', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 13 }}>
                          <strong>{s.invoiceNumber}</strong> — {s.partnerName}
                          <span style={{ color: 'var(--text-secondary)', fontSize: 12, marginLeft: 6 }}>
                            ({fmtHuf(s.openAmountHuf)} nyitott, esedék: {fmtDate(s.dueDate)})
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <Badge color={CONF_COLOR[s.confidence]}>{CONF_LABEL[s.confidence]} ({s.score})</Badge>
                          <Button size="sm" onClick={() => handleMatch(tx.id, s.invoiceId)}>Párosítás</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── History tab ────────────────────────────────────────────────────────────────

const TX_LIMIT = 40;

function HistoryTab() {
  const { toast } = useToast();
  const [rows, setRows] = useState<BankTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (off: number, st: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(TX_LIMIT), offset: String(off) });
      if (st) params.set('status', st);
      const data = await get<{ rows: BankTransaction[]; total: number }>(`/bank-transactions?${params}`);
      setRows(data.rows);
      setTotal(data.total);
    } catch { toast('Betöltési hiba.', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(0, ''); }, [load]);

  function applyFilter(st: string) {
    setStatus(st);
    setOffset(0);
    load(0, st);
  }

  function changePage(dir: 1 | -1) {
    const newOff = offset + dir * TX_LIMIT;
    setOffset(newOff);
    load(newOff, status);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['', 'PAROSITATLAN', 'PAROSITVA', 'KERDESES', 'KIHAGYVA'].map(s => (
          <Button key={s} size="sm" variant={status === s ? 'primary' : 'secondary'} onClick={() => applyFilter(s)}>
            {s ? STATUS_LABEL[s] : 'Összes'}
          </Button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
          Nincs tranzakció.
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  {['Dátum', 'Összeg', 'Partner', 'Közlemény', 'Állapot'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(tx => (
                  <tr key={tx.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtDate(tx.transactionDate)}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: tx.amountHuf >= 0 ? 'var(--success)' : 'var(--error)', fontWeight: 600 }}>
                      {fmtHuf(tx.amountHuf)}
                    </td>
                    <td style={{ padding: '8px 10px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.counterpartyName || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                      {tx.description || tx.reference || '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <Badge color={STATUS_COLOR[tx.status]}>{STATUS_LABEL[tx.status]}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, fontSize: 13, color: 'var(--text-secondary)' }}>
            <span>{total} tranzakció</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => changePage(-1)}>← Előző</Button>
              <Button size="sm" variant="secondary" disabled={offset + TX_LIMIT >= total} onClick={() => changePage(1)}>Következő →</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Fő oldal ───────────────────────────────────────────────────────────────────

export default function BankMatchingPage() {
  const { active, loading: featureLoading } = useFeature('banki_parosito');
  const [refreshKey, setRefreshKey] = useState(0);

  if (featureLoading) return null;
  if (!active) return <LockedFeature featureName="Banki párosító" />;

  return (
    <div>
      <PageHeader title="Banki párosító" subtitle="Banki kivonatok importálása és számlákhoz párosítása" />
      <Tabs
        tabs={[
          {
            label: 'Import',
            content: <UploadTab />,
          },
          {
            label: 'Párosítandók',
            content: <PendingTab key={refreshKey} onMatched={() => setRefreshKey(k => k + 1)} />,
          },
          {
            label: 'Előzmények',
            content: <HistoryTab key={refreshKey} />,
          },
        ]}
      />
    </div>
  );
}
